import React, { useState, useEffect } from 'react';
import { Play, Youtube, ExternalLink, Search, Filter, Clock, Eye, Calendar } from 'lucide-react';
import youtubeApi from '../services/youtubeApi';

const SermonsPage = () => {
  const [allVideos, setAllVideos] = useState({ sermons: [], shorts: [], bible_studies: [] });
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [videoCounts, setVideoCounts] = useState({ all: 0, sermons: 0, shorts: 0, bible_studies: 0 });

  const channelUrl = 'https://www.youtube.com/@ElBethelGlobal';

  const categories = [
    { id: 'all', name: 'All Content', count: videoCounts.all },
    { id: 'sermons', name: 'Sermons', count: videoCounts.sermons },
    { id: 'shorts', name: 'YouTube Shorts', count: videoCounts.shorts },
    { id: 'bible_studies', name: 'Bible Studies', count: videoCounts.bible_studies }
  ];

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        const videos = await youtubeApi.getAllChannelVideos();
        setAllVideos(videos);
        
        // Update category counts
        const counts = {
          all: videos.sermons.length + videos.shorts.length + videos.bible_studies.length,
          sermons: videos.sermons.length,
          shorts: videos.shorts.length,
          bible_studies: videos.bible_studies.length
        };
        setVideoCounts(counts);
        
        // Initially show all videos
        const combined = [...videos.sermons, ...videos.shorts, ...videos.bible_studies];
        setFilteredVideos(combined);
        
      } catch (err) {
        setError('Unable to load videos from YouTube. Please try again later.');
        console.error('YouTube API error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  useEffect(() => {
    // Filter and search videos
    let videos = [];
    
    if (selectedCategory === 'all') {
      videos = [...allVideos.sermons, ...allVideos.shorts, ...allVideos.bible_studies];
    } else {
      videos = allVideos[selectedCategory] || [];
    }

    // Apply search filter
    if (searchTerm.trim()) {
      videos = videos.filter(video =>
        video.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        video.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    switch (sortBy) {
      case 'newest':
        videos.sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
        break;
      case 'oldest':
        videos.sort((a, b) => new Date(a.publishedAt) - new Date(b.publishedAt));
        break;
      case 'most_viewed':
        videos.sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0));
        break;
      case 'duration':
        videos.sort((a, b) => (b.durationSeconds || 0) - (a.durationSeconds || 0));
        break;
      default:
        break;
    }

    console.log(`Filtering - Category: ${selectedCategory}, Videos found: ${videos.length}`);
    setFilteredVideos(videos);
  }, [selectedCategory, searchTerm, sortBy, allVideos]);

  const formatViews = (count) => {
    if (count >= 1000000) return Math.floor(count / 1000000) + 'M';
    if (count >= 1000) return Math.floor(count / 1000) + 'K';
    return count.toString();
  };

  const getCategoryTitle = (category) => {
    switch (category) {
      case 'sermons': return 'Sermons & Teaching';
      case 'shorts': return 'YouTube Shorts';
      case 'bible_studies': return 'Bible Studies';
      default: return 'All Content';
    }
  };

  return (
    <div className="pt-16 lg:pt-20 pb-20 lg:pb-0">
      {/* Hero Section */}
      <section className="py-12 lg:py-20 bg-gradient-to-br from-orange-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 lg:mb-12">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 mb-6 font-display">
              Sermons & Teaching
            </h1>
            <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto mb-6 font-sans">
              Experience powerful biblical messages from El Bethel Church. 
              Watch our latest sermons or join us live every Sunday at 5:00 PM in Grand Prairie, TX.
            </p>
            
            {/* Join Us Live Banner */}
            <div className="max-w-2xl mx-auto bg-gradient-to-r from-[#f17b21] to-orange-600 rounded-xl p-6 text-white">
              <h3 className="text-xl font-bold mb-2 font-display">Join Us Live This Sunday!</h3>
              <p className="mb-4 font-sans">Experience worship in person with biblical teaching and authentic fellowship</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a 
                  href="/visit"
                  className="bg-white text-[#f17b21] hover:bg-orange-50 px-6 py-3 rounded-lg font-semibold transition-colors duration-300"
                >
                  Plan Your Visit
                </a>
                <a 
                  href="https://elbethel.online"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#f17b21] px-6 py-3 rounded-lg font-semibold transition-colors duration-300"
                >
                  Watch Online
                </a>
              </div>
            </div>
          </div>

          {/* YouTube Channel Header */}
          <div className="max-w-2xl mx-auto bg-red-50 border border-red-100 rounded-xl p-6 lg:p-8 text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <Youtube className="w-8 h-8 text-red-600 mr-3" />
              <h3 className="text-xl lg:text-2xl font-bold text-gray-800 font-display">
                El Bethel Global YouTube
              </h3>
            </div>
            <p className="text-gray-600 mb-6 font-sans">
              Subscribe to our YouTube channel for biblical teaching, spiritual insights, and ministry updates.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`${channelUrl}?sub_confirmation=1`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 font-sans"
              >
                <Youtube className="w-5 h-5" />
                <span>Subscribe to Channel</span>
              </a>
              <a
                href={channelUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 font-sans"
              >
                <ExternalLink className="w-5 h-5" />
                <span>Visit Channel</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Search and Filter Controls */}
      <section className="py-8 lg:py-12 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search videos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans"
                />
              </div>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans bg-white"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="most_viewed">Most Viewed</option>
                <option value="duration">Longest First</option>
              </select>
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 lg:px-6 py-2 lg:py-3 rounded-lg font-semibold transition-colors duration-300 font-sans text-sm lg:text-base flex items-center space-x-2 ${
                    selectedCategory === category.id
                      ? 'bg-[#f17b21] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>{category.name}</span>
                  <span className="bg-white/20 px-2 py-1 rounded text-xs">{category.count}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Videos Grid */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-12 h-12 border-4 border-[#f17b21] border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-600 font-sans">Loading videos from El Bethel Global...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 font-sans mb-4">{error}</p>
              <button 
                onClick={() => window.location.reload()}
                className="bg-[#f17b21] hover:bg-[#d86919] text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 font-sans"
              >
                Try Again
              </button>
            </div>
          ) : (
            <>
              {/* Results Header */}
              <div className="mb-8">
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-2 font-display">
                  {getCategoryTitle(selectedCategory)}
                </h2>
                <p className="text-gray-600 font-sans">
                  {filteredVideos.length} video{filteredVideos.length !== 1 ? 's' : ''} found
                  {searchTerm && ` for "${searchTerm}"`}
                </p>
              </div>

              {/* Videos Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {filteredVideos.map((video) => (
                  <div key={video.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                    <div className="relative">
                      <div className="relative w-full" style={{ paddingBottom: '56.25%', height: 0 }}>
                        <iframe
                          src={`${video.embedUrl}?rel=0&modestbranding=1`}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title={video.title}
                          className="absolute top-0 left-0 w-full h-full"
                        />
                      </div>
                      
                      {/* Video Info Overlay */}
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs font-semibold font-sans">
                        {video.duration}
                      </div>
                      
                      {/* Category Badge */}
                      <div className="absolute top-2 left-2">
                        <span className="bg-[#f17b21] text-white px-2 py-1 rounded text-xs font-semibold font-sans">
                          {video.durationSeconds < 60 ? 'Short' : 'Sermon'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="p-6">
                      <h3 className="text-lg font-bold text-gray-800 mb-3 font-display line-clamp-2">
                        {video.title}
                      </h3>
                      <p className="text-gray-600 mb-4 leading-relaxed font-sans text-sm line-clamp-3">
                        {video.description}
                      </p>
                      
                      {/* Video Metadata */}
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-4 font-sans">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-3 h-3" />
                            <span>{video.publishDate}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Eye className="w-3 h-3" />
                            <span>{formatViews(video.viewCount)} views</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Clock className="w-3 h-3" />
                          <span>{video.duration}</span>
                        </div>
                      </div>

                      <a
                        href={video.watchUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-2 text-[#f17b21] hover:text-[#d86919] font-semibold transition-colors duration-300 font-sans text-sm"
                      >
                        <Play className="w-4 h-4" />
                        <span>Watch on YouTube</span>
                      </a>
                    </div>
                  </div>
                ))}
              </div>

              {filteredVideos.length === 0 && !loading && (
                <div className="text-center py-12">
                  <p className="text-gray-600 font-sans">No videos found matching your criteria.</p>
                  <button 
                    onClick={() => { setSearchTerm(''); setSelectedCategory('all'); }}
                    className="mt-4 text-[#f17b21] hover:text-[#d86919] font-semibold font-sans"
                  >
                    Clear filters
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default SermonsPage;